import React, { useContext, useEffect, useMemo, useState } from "react";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { BillingContext } from "contexts/BillingContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { useHistory } from "react-router-dom";
import {
  displayGpuHours,
  formatGrantHeadline,
  formatProgramDaysLeft,
  formatProgramEndDate,
  getProgramDaysRemaining,
  shouldShowAcademicGrantAccess,
} from "components/utils";
import GPUHoursBalance from "components/GPUHoursBalance";
import { useEnvironmentBurnRateBreakdown } from "components/Environment/utils";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import Spinner from "components/Graphs/InlineSpinner";

const Balance: React.FC = (props) => {
  const billingContext = useContext(BillingContext);
  const [gpuHoursBalance, setGpuHoursBalance] = useState<string>("0 GPU hours");
  const history = useHistory();
  const [loadingGpuHoursRange, setLoadingGpuHoursRange] =
    useState<boolean>(false);
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const workspaceContext = useContext(WorkspaceContext);
  const showGrantAccess = shouldShowAcademicGrantAccess(
    orgContext.activeOrg,
    instanceTypesContext.currencyDisplay,
  );
  const grantDaysLeft = showGrantAccess
    ? formatProgramDaysLeft(orgContext.activeOrg)
    : null;

  const burnBreakdown = useEnvironmentBurnRateBreakdown(
    workspaceContext.environments,
  );

  const isDataLoading =
    orgContext.isLoading ||
    !orgContext.activeOrgId ||
    billingContext.isBalanceLoading;
  const isWorkspaceLoading =
    !billingContext.isBalanceLoading && workspaceContext.isLoading;

  const burnRateColor = useMemo(() => {
    const colors = {
      highlight: { bg: "bg-highlight", text: "text-highlight" },
      yellow: { bg: "bg-yellow-500", text: "text-yellow-500" },
      red: { bg: "bg-red-500", text: "text-red-500" },
    };
    if (burnBreakdown.total === 0) return colors.highlight;
    const hoursRemaining = billingContext.currentBalance / burnBreakdown.total;
    if (hoursRemaining > 24) return colors.highlight;
    if (hoursRemaining > 6) return colors.yellow;
    return colors.red;
  }, [burnBreakdown.total, billingContext.currentBalance]);

  useEffect(() => {
    if (showGrantAccess || instanceTypesContext.currencyDisplay === "credits") {
      return;
    }
    setLoadingGpuHoursRange(true);

    const rangeStart = displayGpuHours(billingContext.gpuHoursMostExpensive);
    if (billingContext.gpuHoursMostExpensive < 0) {
      setGpuHoursBalance("<1 GPU hour");
    } else if (
      billingContext.gpuHoursLeastExpensive === 0 ||
      billingContext.gpuHoursMostExpensive ===
        billingContext.gpuHoursLeastExpensive
    ) {
      // Org has > 0 credits, but not enough to run any instances
      setGpuHoursBalance(`${rangeStart} GPU hours`);
    } else {
      const rangeEnd = displayGpuHours(billingContext.gpuHoursLeastExpensive);
      setGpuHoursBalance(`${rangeStart}-${rangeEnd} GPU hours`);
    }

    setLoadingGpuHoursRange(false);
  }, [
    billingContext.gpuHoursMostExpensive,
    billingContext.gpuHoursLeastExpensive,
    instanceTypesContext.currencyDisplay,
    showGrantAccess,
  ]);

  return (
    <TooltipPrimitives.Provider>
      <TooltipPrimitives.Root delayDuration={0}>
        <TooltipPrimitives.Trigger asChild>
          <div
            className="group flex flex-row items-center text-secondary border border-secondary rounded-md p-[6px] border-zinc-800 px-2 cursor-pointer min-w-[60px] text-center min-h-[34px] hover:brightness-150"
            onClick={() => {
              history.push(`/org/billing?openAddCredits=true`);
            }}
          >
            {isDataLoading ? (
              <Spinner className="h-3 w-3 mx-auto" />
            ) : showGrantAccess ? (
              <span className="text-xs font-mono text-secondary flex flex-row items-center group-hover:brightness-150">
                {grantDaysLeft || "—"}
              </span>
            ) : (
              <>
                {isWorkspaceLoading ? (
                  <Spinner className="h-2 w-2 mr-2" />
                ) : (
                  burnBreakdown.total > 0 && (
                    <span className="relative flex h-2 w-2 mr-2">
                      <span
                        className={`animate-ping absolute inline-flex h-full w-full rounded-full ${burnRateColor.bg} opacity-50`}
                      />
                      <span
                        className={`relative inline-flex rounded-full h-2 w-2 ${burnRateColor.bg}`}
                      />
                    </span>
                  )
                )}
                {billingContext.currentBalance > 0 && !loadingGpuHoursRange ? (
                  <span className="text-xs font-mono text-secondary flex flex-row items-center group-hover:brightness-150">
                    {instanceTypesContext.currencyDisplay === "credits" &&
                      billingContext.currentBalance.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    {instanceTypesContext.currencyDisplay === "hours" && (
                      <GPUHoursBalance balanceRange={gpuHoursBalance} />
                    )}
                  </span>
                ) : (
                  <>
                    {billingContext.currentBalance === 0 ? (
                      <span className="text-xs font-mono flex flex-row items-center group-hover:brightness-150">
                        Add Credits{" "}
                        <ArrowUpRightIcon className="w-3 h-3 ml-[1px]" />
                      </span>
                    ) : (
                      <span className="text-xs text-red-500 font-mono group-hover:brightness-150">
                        {billingContext.currentBalance.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </TooltipPrimitives.Trigger>
        {!isDataLoading &&
          (showGrantAccess ||
            (!isWorkspaceLoading &&
              billingContext.currentBalance > 0 &&
              (burnBreakdown.total > 0 ||
                instanceTypesContext.currencyDisplay === "hours"))) && (
            <TooltipPrimitives.Portal>
              <TooltipPrimitives.Content
                side="top"
                sideOffset={4}
                className="z-50 w-56 rounded-md bg-zinc-800 px-3 py-2.5 text-sm text-white shadow-md"
              >
                {showGrantAccess ? (
                  <p className="text-zinc-400 leading-relaxed">
                    {formatGrantHeadline(
                      formatProgramEndDate(orgContext.activeOrg),
                      getProgramDaysRemaining(orgContext.activeOrg),
                    )}
                  </p>
                ) : null}

                {/* GPU hours note — grant orgs show days remaining, not this estimate */}
                {!showGrantAccess &&
                instanceTypesContext.currencyDisplay === "hours" ? (
                  <>
                    <p className="text-zinc-400 leading-relaxed">
                      Balance based on potential usage of:
                    </p>
                    <ul className="list-disc list-inside">
                      <li className="text-zinc-300">
                        {
                          instanceTypesContext.mostExpensiveAvailableInstance
                            ?.type
                        }{" "}
                        <span className="text-zinc-400">for</span>{" "}
                        {billingContext.gpuHoursMostExpensive} hrs
                      </li>
                      <li className="text-zinc-300">
                        {
                          instanceTypesContext.leastExpensiveAvailableInstance
                            ?.type
                        }{" "}
                        <span className="text-zinc-400">for</span>{" "}
                        {billingContext.gpuHoursLeastExpensive} hrs
                      </li>
                    </ul>
                    {burnBreakdown.total > 0 && (
                      <div className="border-t border-zinc-700 my-2" />
                    )}
                  </>
                ) : null}

                {/* Burn rate — grant orgs show only the access headline */}
                {!showGrantAccess && burnBreakdown.total > 0 && (
                  <>
                    <div
                      className={`flex gap-4 ${instanceTypesContext.currencyDisplay === "credits" && billingContext.currentBalance > 0 ? "justify-between" : ""}`}
                    >
                      <div>
                        <p className="text-zinc-400 uppercase tracking-wider text-xs font-semibold mb-1.5">
                          Burn Rate
                        </p>
                        <p className="text-white text-sm font-semibold">
                          ${burnBreakdown.total.toFixed(2)}
                          <span className="text-zinc-400 font-normal">
                            {" "}
                            / hr
                          </span>
                        </p>
                      </div>
                      {instanceTypesContext.currencyDisplay === "credits" &&
                        billingContext.currentBalance > 0 && (
                          <>
                            <div className="border-l border-zinc-700 self-stretch" />
                            <div className="text-right">
                              <p className="text-zinc-400 uppercase tracking-wider text-xs font-semibold mb-1.5">
                                Credits Left
                              </p>
                              <p
                                className={`text-sm font-semibold ${burnRateColor.text}`}
                              >
                                {(
                                  billingContext.currentBalance /
                                  burnBreakdown.total
                                ).toFixed(1)}
                                <span className="text-zinc-400 font-normal text-xs">
                                  {" "}
                                  hrs
                                </span>
                              </p>
                            </div>
                          </>
                        )}
                    </div>

                    {(burnBreakdown.computeCost > 0 ||
                      burnBreakdown.storageCost > 0) && (
                      <>
                        <div className="border-t border-zinc-700 my-2" />
                        <p className="text-zinc-400 uppercase tracking-wider text-xs font-semibold mb-1.5">
                          All Environments
                        </p>
                        <div className="flex flex-col gap-1">
                          {burnBreakdown.computeCost > 0 && (
                            <div className="flex items-baseline gap-1">
                              <span className="text-zinc-400">Compute</span>
                              <span className="flex-1 border-b border-dotted border-zinc-600 mb-[3px]" />
                              <span className="text-zinc-300 font-mono text-[13px]">
                                ${burnBreakdown.computeCost.toFixed(2)}/hr
                              </span>
                            </div>
                          )}
                          {burnBreakdown.storageCost > 0 && (
                            <div className="flex items-baseline gap-1">
                              <span className="text-zinc-400">Storage</span>
                              <span className="flex-1 border-b border-dotted border-zinc-600 mb-[3px]" />
                              <span className="text-zinc-300 font-mono text-[13px]">
                                ${burnBreakdown.storageCost.toFixed(2)}/hr
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                <TooltipPrimitives.Arrow className="fill-zinc-800" />
              </TooltipPrimitives.Content>
            </TooltipPrimitives.Portal>
          )}
      </TooltipPrimitives.Root>
    </TooltipPrimitives.Provider>
  );
};

export default Balance;
